import i18n from '../i18n'
import { faDigits } from './numerals'

/**
 * The home screen's rules (design page 3, `home`; P-home of the go-live
 * plan), mirrored from the Android app's `HomeLogic.kt` so both clients
 * put the same lesson on the big card and count the same lessons as
 * remaining. Inputs are the wire shapes: `LessonListItemDto[]` and
 * `AttemptListItemDto[]` (newest first, as `attempts/list` sends it).
 */

const t = (key, options) => i18n.t(key, { ns: 'home', ...options })

/** Getters, not plain values: the active language decides which label comes back. */
export const LEVEL_LABELS = {
  get beginner() {
    return t('levels.beginner')
  },
  get intermediate() {
    return t('levels.intermediate')
  },
  get advanced() {
    return t('levels.advanced')
  },
}

/** 0..1 — the accent bar under the card: sentences attempted over the lesson's count. */
function progressOf(lesson, done) {
  if (!lesson.sentenceCount || lesson.sentenceCount <= 0) return 0
  return Math.min(1, Math.max(0, done / lesson.sentenceCount))
}

/**
 * The lesson to put first on the home screen.
 *
 * The most recent attempt decides, because that is what "continue" means:
 * the lesson the learner was in last, at its first sentence they have not
 * tried yet. When that lesson is finished — every sentence has an attempt —
 * the card moves on to the first lesson in catalogue order nobody has
 * started. Nothing left to start returns null and the card is not drawn;
 * "continue" with nothing to continue is the kind of card that gets tapped
 * once and never trusted again.
 */
export function continueTarget(lessons, history) {
  const byId = new Map(lessons.map((lesson) => [lesson.id, lesson]))
  const recent = history.find((attempt) => byId.has(attempt.lessonId))
  if (recent) {
    const lesson = byId.get(recent.lessonId)
    const attempted = new Set(history.filter((attempt) => attempt.lessonId === lesson.id).map((attempt) => attempt.sentenceIndex))
    let next = null
    for (let index = 0; index < lesson.sentenceCount; index++) {
      if (!attempted.has(index)) {
        next = index
        break
      }
    }
    if (next !== null) {
      const done = [...attempted].filter((index) => index < lesson.sentenceCount).length
      return { lesson, sentenceIndex: next, done, fresh: false, progress: progressOf(lesson, done) }
    }
  }
  const started = new Set(history.map((attempt) => attempt.lessonId))
  const untouched = lessons.find((lesson) => lesson.sentenceCount > 0 && lesson.lastScore == null && !started.has(lesson.id))
  if (!untouched) return null
  return { lesson: untouched, sentenceIndex: 0, done: 0, fresh: true, progress: 0 }
}

/**
 * The two rows under «پیشنهاد امروز»: lessons not yet scored, skipping the
 * one the big card already shows, topped up with scored ones when the
 * catalogue is small enough that two are all there are.
 */
export function suggestedLessons(lessons, exclude, count = 2) {
  const rest = lessons.filter((lesson) => lesson.id !== exclude?.id)
  const fresh = rest.filter((lesson) => lesson.lastScore == null)
  const scored = rest.filter((lesson) => lesson.lastScore != null)
  return [...fresh, ...scored].slice(0, count)
}

/** «امروز N درس برایت مانده»: the lessons without a score yet. */
export function lessonsRemaining(lessons) {
  return lessons.filter((lesson) => lesson.lastScore == null).length
}

/** «سلام بردیا» from the server's display name, «سلام!» without one. */
export function greetingFor(user) {
  const name = String(user?.displayName ?? '').trim()
  return name ? t('greeting.named', { name }) : t('greeting.plain')
}

/**
 * The line under the greeting. An empty catalogue is the day-one case and
 * says so; a catalogue the learner has finished is not "nothing published"
 * — that sentence would be a lie the Android app happens to tell — so it
 * gets its own.
 */
export function remainingLine(lessons) {
  if (lessons.length === 0) return t('remaining.none')
  const remaining = lessonsRemaining(lessons)
  return remaining > 0 ? t('remaining.some', { count: faDigits(remaining) }) : t('remaining.done')
}

/** The lessons page shows the card under «همه» and under the card lesson's own level only. */
export function showContinueCard(target, level) {
  return Boolean(target) && (level == null || target.lesson.level === level)
}
